import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
  inject,
  input,
} from '@angular/core';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { SortDirection } from '@core/search-types';
import type { SortColumnId, SortOption, SortValue } from '@core/search-types';
import type { IconName, RtButtonVariant } from '@core/user-interface-legacy';
import {
  DropdownWidth,
  RtStatefulComponent,
  WindowRef,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  IconComponent,
  OverlayOriginDirective,
  BadgeComponent,
} from '@core/user-interface-legacy';

type State = {
  dropdownOpen: boolean;
};

@Component({
  selector: 'reports-sort-by-button',
  templateUrl: './sort-by-button.component.html',
  styleUrls: ['./sort-by-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    IconComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    TranslatePipe,
    BadgeComponent,
  ],
})
export class SortByButtonComponent extends RtStatefulComponent<State> {
  private readonly windowRef = inject(WindowRef);
  private readonly i18n = inject(I18nService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly options = input<SortOption[]>([]);
  public readonly buttonSize = input<('small' | 'default') | undefined>('small');
  public readonly allowReset = input<boolean>(false);
  public readonly withSelectButtonStyle = input<boolean>(false);
  public readonly dropdownWithScroll = input<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly initialSortDirection = input<
    SortDirection | ((option: SortOption) => SortDirection)
  >(SortDirection.Asc);
  public readonly noButtonLabel = input(false);
  public readonly variant = input<Extract<RtButtonVariant, 'tertiary' | 'outline'>>('tertiary');
  @Input() public dropdownHeader?: string;
  @Input() public value?: SortValue;
  @Input() public dropdownWidth: DropdownWidth = DropdownWidth.SmallX;

  public valueChange = output<SortValue>();

  @ViewChild('sortByButtonDropdownTrigger', { static: true }) private dropdownContainer: any;

  private ngUnsubscribe = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      dropdownOpen: false,
    });
  }

  public ngAfterViewInit() {
    if (this.withSelectButtonStyle()) {
      this.adjustDropdownWidth();

      setTimeout(() => {
        this.adjustDropdownWidth();
        this.cdRef.detectChanges();
      }, 0);

      observableFromEvent(this.windowRef.nativeWindow, 'resize')
        .pipe(takeUntil(this.ngUnsubscribe), debounceTime(150))
        .subscribe(() => this.adjustDropdownWidth());
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public toggleDropdown(open: boolean): void {
    this.setState({
      ...this.state,
      dropdownOpen: open,
    });
  }

  public getIcon(
    direction: SortDirection,
  ): Extract<IconName, 'sort-ascending' | 'sort-descending'> {
    return direction === SortDirection.Asc ? 'sort-ascending' : 'sort-descending';
  }

  public getValueLabel(columnId: SortColumnId): string {
    const option = this.options().find(o => o.columnId === columnId);
    return option ? option.label : this.i18n.translate('app.common.sorting_no_sorting');
  }

  public handleOptionClick(option: SortOption): void {
    this.valueChange.emit({
      columnId: option.columnId!,
      direction: this.getOptionDirection(option),
    });
    this.toggleDropdown(false);
  }

  public handleResetClick(): void {
    this.valueChange.emit(<any>{
      columnId: undefined,
      direction: undefined,
    });

    this.toggleDropdown(false);
  }
  public trackByColumnId(index: number, option: SortOption): string {
    return option.columnId!;
  }

  private getOptionDirection(option: SortOption): SortDirection {
    const initialSortDirection = this.initialSortDirection();
    let direction =
      typeof initialSortDirection === 'function'
        ? initialSortDirection(option)
        : initialSortDirection;
    if (this.value && this.value.columnId === option.columnId) {
      direction =
        this.value.direction === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc;
    }
    if (option.allowOnlyOneDirection) {
      direction = option.allowOnlyOneDirection;
    }
    return direction;
  }

  private adjustDropdownWidth(): void {
    this.dropdownWidth = this.dropdownContainer.elementRef.nativeElement.offsetWidth;
  }
}
